import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, Filter, Loader2, Wallet, X } from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useAllClaims, useMarkClaimPaid } from "../hooks";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import type { ExpenseClaimSummary } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) { return `KES ${n.toLocaleString()}`; }

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function daysSince(s?: string | null) {
  if (!s) return null;
  const diff = Date.now() - new Date(s).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function typeLabel(t: string) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const PAGE_SIZE = 20;

// ─── inline pay button ────────────────────────────────────────────────────────

function MarkPaidButton({ claim }: { claim: ExpenseClaimSummary }) {
  const markPaid = useMarkClaimPaid();
  const confirm = useConfirm();
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        if (!(await confirm(`Mark ${claim.claimNo} as paid?\n\nEmployee: ${claim.employeeName}\nAmount: ${fmt(claim.totalAmount)}`))) return;
        await markPaid.mutateAsync(claim.id);
      }}
      disabled={markPaid.isPending}
      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-800 disabled:opacity-50"
    >
      <Wallet className="h-3.5 w-3.5" />
      {markPaid.isPending ? "Processing…" : "Mark as Paid"}
    </button>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

type Tab = "awaiting" | "history";

export default function PaidClaimsPage() {
  const navigate = useNavigate();
  const { hasRole, hasAnyRole, hasPermission } = useAuth();

  const isAdmin     = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const canMarkPaid = isAdmin || hasPermission("HR_CLAIM_APPROVE") || hasPermission("FINANCE_CLAIM_APPROVE");

  if (!canMarkPaid) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-8 text-center">
        <p className="font-bold text-red-700">Access denied.</p>
        <p className="mt-1 text-sm text-red-500">You do not have permission to process claim payments.</p>
        <Link to="/claims" className="mt-3 inline-block text-xs font-bold text-red-600 hover:underline">
          ← Back to dashboard
        </Link>
      </div>
    );
  }

  const [tab, setTab]       = useState<Tab>("awaiting");
  const [page, setPage]     = useState(0);
  const [search, setSearch] = useState("");

  // Awaiting payment = APPROVED status
  const awaitingQuery = useAllClaims({
    page,
    size: PAGE_SIZE,
    status: "APPROVED",
    ...(search && { search }),
  });

  // Payment history = PAID status
  const historyQuery = useAllClaims({
    page,
    size: PAGE_SIZE,
    status: "PAID",
    ...(search && { search }),
  });

  const activeQuery   = tab === "awaiting" ? awaitingQuery : historyQuery;
  const claims        = activeQuery.data?.content ?? [];
  const total         = activeQuery.data?.totalElements ?? 0;
  const totalPages    = activeQuery.data?.totalPages ?? 0;
  const isLoading     = activeQuery.isLoading;

  const totalAmt      = claims.reduce((s, c) => s + (c.totalAmount ?? 0), 0);

  // Summary counts from both queries (available even when on history tab)
  const awaitingCount = awaitingQuery.data?.totalElements ?? 0;
  const awaitingAmt   = (awaitingQuery.data?.content ?? []).reduce((s, c) => s + (c.totalAmount ?? 0), 0);

  function switchTab(t: Tab) {
    setTab(t);
    setPage(0);
  }

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Paid Claims</h1>
        </div>
        <Link to="/claims" className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
          ← Dashboard
        </Link>
      </div>

      {/* ── Awaiting payment banner ── */}
      {awaitingCount > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-200">
              <Clock className="h-5 w-5 text-indigo-700" />
            </div>
            <div>
              <p className="text-sm font-black text-indigo-900">
                {awaitingCount} claim{awaitingCount !== 1 ? "s" : ""} awaiting payment
              </p>
              <p className="text-xs text-indigo-600">
                Total on this page: {fmt(awaitingAmt)}
              </p>
            </div>
          </div>
          {tab !== "awaiting" && (
            <button
              onClick={() => switchTab("awaiting")}
              className="rounded-xl bg-indigo-700 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-800"
            >
              View queue →
            </button>
          )}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex gap-1 rounded-xl border bg-slate-50 p-1">
        <button
          onClick={() => switchTab("awaiting")}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
            tab === "awaiting"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Clock className="h-4 w-4" />
          Awaiting Payment
          {awaitingCount > 0 && (
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-black text-indigo-700">
              {awaitingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => switchTab("history")}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
            tab === "history"
              ? "bg-white text-emerald-700 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <CheckCircle2 className="h-4 w-4" />
          Payment History
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
        <Filter className="h-4 w-4 shrink-0 self-center text-slate-400" />
        <input
          type="text"
          placeholder="Search employee or claim no…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={() => { setSearch(""); setPage(0); }}
            className="flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        {total > 0 && (
          <span className="ml-auto text-xs font-semibold text-slate-400">
            {total} result{total !== 1 ? "s" : ""} · {fmt(totalAmt)}
          </span>
        )}
      </div>

      {/* ── Table ── */}
      {isLoading ? (
        <div className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-8 text-slate-400 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3">Claim</th>
                <th className="px-3 py-3">Employee</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Amount</th>
                <th className="px-3 py-3">
                  {tab === "awaiting" ? "Approved" : "Paid"}
                </th>
                {tab === "awaiting" && <th className="px-3 py-3">Days pending</th>}
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {claims.length === 0 ? (
                <tr>
                  <td colSpan={tab === "awaiting" ? 8 : 7} className="px-5 py-10 text-center text-sm text-slate-400">
                    {tab === "awaiting"
                      ? "No claims awaiting payment."
                      : "No paid claims yet."}
                  </td>
                </tr>
              ) : claims.map((c) => (
                <ClaimRow
                  key={c.id}
                  claim={c}
                  tab={tab}
                  onView={() => navigate(`/claims/${c.id}`)}
                />
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t bg-slate-50 px-5 py-3">
            <span className="text-xs text-slate-400">
              {total} claim{total !== 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-xs font-bold text-slate-500">
                {page + 1} / {totalPages || 1}
              </span>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── row component ────────────────────────────────────────────────────────────

function ClaimRow({
  claim,
  tab,
  onView,
}: {
  claim: ExpenseClaimSummary;
  tab: Tab;
  onView: () => void;
}) {
  const days = daysSince(claim.approvedAt);

  return (
    <tr className="transition-colors hover:bg-slate-50">
      {/* Claim no + icon */}
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-xs font-black text-indigo-700">
            {claim.claimType.charAt(0)}
          </div>
          <span className="text-xs font-bold text-slate-500">{claim.claimNo}</span>
        </div>
      </td>
      <td className="px-3 py-3 font-semibold text-slate-700">{claim.employeeName}</td>
      <td className="px-3 py-3 text-slate-600">{typeLabel(claim.claimType)}</td>
      <td className="px-3 py-3 font-black text-slate-800">{`KES ${claim.totalAmount.toLocaleString()}`}</td>
      <td className="px-3 py-3 text-xs text-slate-400">
        {tab === "awaiting" ? fmtDate(claim.approvedAt) : fmtDate(claim.submittedAt)}
      </td>
      {tab === "awaiting" && (
        <td className="px-3 py-3">
          {days != null ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                days > 7
                  ? "bg-red-100 text-red-700"
                  : days > 3
                  ? "bg-amber-100 text-amber-700"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              {days}d
            </span>
          ) : "—"}
        </td>
      )}
      <td className="px-3 py-3">
        <ClaimStatusBadge status={claim.status} />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-2">
          {tab === "awaiting" && <MarkPaidButton claim={claim} />}
          <button
            onClick={onView}
            className="rounded-lg border px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100"
          >
            View →
          </button>
        </div>
      </td>
    </tr>
  );
}
